import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  Button,
  checkIfRecaptchaEnabled,
  Form,
  TEST_GRECAPTCHA,
  TextLink,
  theme,
  useNoticeBannersStackContext,
  useReCaptcha,
} from '@sweetgreen/garnish';

import { useAuthFlow } from '@order/AuthFlow';
import { useIsLoggedIn } from '@order/AuthMachine';
import { PageWrapper } from '@order/components';
import { useCustomer } from '@order/Customer';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import {
  useCloseActiveScreensOnAuthStatusChange,
  useRedirectAfterAuthStateChange,
} from '../../navigation';
import type { LoginScreenProps } from '../../navigation/AppNavigation.props';
import type { LoginFormData } from './hooks';
import {
  useLogIn,
  useLoginForm,
  useShouldUseDecoupledCustomerLogin,
} from './hooks';

// ────────────────────────────────────────────────────────────────────────────────

export const LoginScreen = ({ navigation, route }: LoginScreenProps) => {
  const { redirect: redirectParam } = route.params ?? {};
  const { customer, isFetchingCustomer, signIn: handleSignIn } = useCustomer();
  const isLoggedIn = useIsLoggedIn();
  const { track } = useTelemetry();

  const shouldUseAuthFlowRework = useFeatureFlag(
    'cels-763-short-rework-the-authentication-redirect-flow-enabled',
  );
  const { redirectAfterSignIn } = useAuthFlow();
  const redirectAfterAuthStateChange = useRedirectAfterAuthStateChange();

  // ─────────────────────────────────────────────────────────────────

  const handlePressJoin = useCallback(() => {
    navigation.navigate('Join', { redirect: redirectParam });
  }, [navigation, redirectParam]);

  const handlePressForgotPassword = useCallback(() => {
    navigation.navigate('ForgotPassword', { redirect: redirectParam });
  }, [navigation, redirectParam]);

  // ─────────────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    if (isFetchingCustomer) return;

    if (isLoggedIn && shouldUseAuthFlowRework) {
      redirectAfterAuthStateChange();
    }

    if (isLoggedIn && !shouldUseAuthFlowRework) {
      redirectAfterSignIn(redirectParam);
    }

    track('sign-in.success', { userId: customer?.id });
  }, [
    isFetchingCustomer,
    isLoggedIn,
    redirectParam,
    track,
    redirectAfterAuthStateChange,
    customer?.id,
    shouldUseAuthFlowRework,
    redirectAfterSignIn,
  ]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <LoginForm
      testID="login-form"
      signIn={handleSignIn}
      onPressJoin={handlePressJoin}
      onPressForgotPassword={handlePressForgotPassword}
    />
  );
};

export const LoginForm = (props: LoginFormProps) => {
  const { testID, signIn, onPressJoin, onPressForgotPassword } = props;

  const { isSigningIn, isFetchingCustomer } = useCustomer();

  // ────────────────────────────────────────────────────────────────────

  const { form, showVerificationErrors } = useLoginForm();
  const { control, handleSubmit, formState } = form;
  const { errors, isSubmitting } = formState;

  // ─── OPS-customer-decoupling-login-enabled ───────────────────────────

  const shouldUseDecoupledCustomerLogin = useShouldUseDecoupledCustomerLogin();
  const { logIn, isLoggingIn } = useLogIn({
    onValidationError: showVerificationErrors,
  });

  const logInCustomer = shouldUseDecoupledCustomerLogin ? logIn : signIn;
  const isCustomerLoggingIn = shouldUseDecoupledCustomerLogin
    ? isLoggingIn
    : isSigningIn;

  // ─────────────────────────────────────────────────────────────────────

  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  // ─── ReCaptcha ───────────────────────────────────────────────────

  const { reCaptchaMachine, reCaptchaRef } = useReCaptcha();

  const [reCaptchaState] = reCaptchaMachine;
  const isLoadingRecaptcha = reCaptchaState.matches('loading');
  const isExecutingRecaptcha = reCaptchaState.matches('executing');

  // ─── Helpers ────────────────────────────────────────────────────────

  const shouldUseAuthFlowRework = useFeatureFlag(
    'cels-763-short-rework-the-authentication-redirect-flow-enabled',
  );
  const closeActiveScreensOnAuthStatusChange =
    useCloseActiveScreensOnAuthStatusChange();

  const signInUser = useCallback(
    async (formData: LoginFormData) => {
      const { email, password, grecaptcha = TEST_GRECAPTCHA } = formData;

      const logInResponse = await logInCustomer({
        email,
        password,
        grecaptcha,
      });

      const logInResponseTypeName = logInResponse?.__typename;
      const isSuccessfullyLoggedIn = logInResponseTypeName === 'LoginSuccess';

      // close other open screens and modals, to prevent unnecessary network calls and state updates
      if (isSuccessfullyLoggedIn && shouldUseAuthFlowRework)
        closeActiveScreensOnAuthStatusChange();

      // reset recaptcha after request
      reCaptchaRef.current?.resetRecaptcha();
    },
    [
      logInCustomer,
      shouldUseAuthFlowRework,
      closeActiveScreensOnAuthStatusChange,
      reCaptchaRef,
    ],
  );

  const onRecaptchaVerify = useCallback(
    async (grecaptcha: string) => {
      if (!grecaptcha) return;

      const formValues = form.getValues();

      await signInUser({ ...formValues, grecaptcha });
    },
    [form, signInUser],
  );

  const onRecaptchaError = useCallback(() => {
    addNoticeBanner({ text: t('general.error'), palette: 'caution' });
  }, [addNoticeBanner, t]);

  const submitForm = useMemo(() => {
    const isRecaptchaEnabled = checkIfRecaptchaEnabled();

    return handleSubmit(async (formData) => {
      await (isRecaptchaEnabled
        ? reCaptchaRef.current?.executeRecaptcha()
        : signInUser(formData));
    });
  }, [handleSubmit, reCaptchaRef, signInUser]);

  // ────────────────────────────────────────────────────────────────────

  const shouldShowLoadingSubmitBtn =
    isSubmitting ||
    isCustomerLoggingIn ||
    isExecutingRecaptcha ||
    isFetchingCustomer;

  // ─────────────────────────────────────────────────────────────────

  return (
    <PageWrapper>
      <Form.Container
        testID={testID}
        reCaptchaRef={reCaptchaRef}
        reCaptchaMachine={reCaptchaMachine}
        onRecaptchaVerify={onRecaptchaVerify}
        onRecaptchaError={onRecaptchaError}
      >
        <Form.Title>{t('login.title')}</Form.Title>

        <Form.Subtitle>
          <BodyText size={3}>{t('login.subtitle')} </BodyText>

          <View style={styles.joinBtnWrapper}>
            <Button
              onPress={onPressJoin}
              palette="secondary"
              testID="button-join-now"
            >
              {t('login.join-button')}
            </Button>
          </View>
        </Form.Subtitle>

        {/* ========== email ========== */}

        <Form.Group>
          <Form.TextField
            name="email"
            keyboardType="email-address"
            control={control}
            rules={{ required: true }}
            testID="field-email"
            label={t('form.email')}
            hideSubmitButton
            onSubmit={submitForm}
            autoComplete="email"
            notice={errors.email?.message}
          />
        </Form.Group>

        {/* ========== password ========== */}

        <Form.Group>
          <Form.TextFieldPassword
            name="password"
            control={control}
            rules={{ required: true }}
            testID="field-password"
            label={t('form.password')}
            hideSubmitButton
            onSubmit={submitForm}
            autoComplete="password"
            notice={errors.password?.message}
            toggleTestID="field-password-toggle"
          />
        </Form.Group>

        {/* ========== ========== ========== */}

        <Form.SubmitButton
          testID="button-sign-in"
          onPress={submitForm}
          isLoading={shouldShowLoadingSubmitBtn}
          disabled={isLoadingRecaptcha}
        >
          {t('login.sign-in-button')}
        </Form.SubmitButton>

        {/* ========== ========== ========== */}

        <View style={styles.forgotPasswordWrapper}>
          <TextLink
            onPress={onPressForgotPassword}
            size={4}
            testID="link-forgot-password"
          >
            {t('login.forgot-password-button')}
          </TextLink>
        </View>
      </Form.Container>
    </PageWrapper>
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type LoginFormProps = {
  readonly signIn: ReturnType<typeof useCustomer>['signIn'];
  readonly onPressJoin: () => void;
  readonly onPressForgotPassword: () => void;
} & Pick<ViewProps, 'testID'>;

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  joinBtnWrapper: {
    alignItems: 'flex-start',
    paddingTop: theme.spacing.medium,
  },
  forgotPasswordWrapper: {
    alignItems: 'flex-start',
    paddingTop: theme.spacing.xlarge,
  },
});
